import React, { useMemo } from 'react';
import { Card, Space, Typography } from 'antd';
import type { ExecutionEdgeHydration, ExecutionStopHydration } from './types';
import type { EnrichedExecutionCargo } from '../routes/execution/cargoHumanization';
import PartialExecutionCargoRow from './PartialExecutionCargoRow';
import { isLoadingStop } from './executionHelpers';

const { Text } = Typography;

type Props = {
  stops: ExecutionStopHydration[];
  cargo: EnrichedExecutionCargo[];
  edges: ExecutionEdgeHydration[];
};

export const CargoExecutionGraph: React.FC<Props> = ({ stops, cargo, edges }) => {
  const uidLabels = useMemo(() => {
    const loading = stops.filter((s) => isLoadingStop(s.type));
    const unloading = stops.filter((s) => !isLoadingStop(s.type));
    const map = new Map<string, string>();
    loading.forEach((s, i) => map.set(s.executionStopUid, `Погрузка ${i + 1}`));
    unloading.forEach((s, i) => map.set(s.executionStopUid, `Выгрузка ${i + 1}`));
    return map;
  }, [stops]);

  if (cargo.length === 0) {
    return (
      <Card size="small" title="Исполнение грузов">
        <Text type="secondary">Нет распределённых грузов.</Text>
      </Card>
    );
  }

  const rows =
    edges.length > 0
      ? edges.map((edge) => ({
          key: `${edge.cargoId}-${edge.fromExecutionStopUid}-${edge.toExecutionStopUid}`,
          edge,
          cargoRow: cargo.find(
            (c) =>
              c.cargoId === edge.cargoId &&
              c.fromExecutionStopUid === edge.fromExecutionStopUid &&
              c.toExecutionStopUid === edge.toExecutionStopUid
          )
        }))
      : cargo.map((c) => ({
          key: `${c.cargoId}-${c.fromExecutionStopUid}-${c.toExecutionStopUid}`,
          edge: {
            fromExecutionStopUid: c.fromExecutionStopUid,
            toExecutionStopUid: c.toExecutionStopUid,
            cargoId: c.cargoId,
            quantity: c.plannedQty
          },
          cargoRow: c
        }));

  return (
    <Card size="small" title="Исполнение грузов">
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {rows.map(({ key, edge, cargoRow }) =>
          cargoRow ? (
            <PartialExecutionCargoRow
              key={key}
              cargo={cargoRow}
              fromLabel={uidLabels.get(edge.fromExecutionStopUid) ?? 'Погрузка'}
              toLabel={uidLabels.get(edge.toExecutionStopUid) ?? 'Выгрузка'}
              compact
            />
          ) : null
        )}
      </Space>
    </Card>
  );
};

export default CargoExecutionGraph;
